import { AbstractManager } from "./AbstractManager.js";
import { getComponentElements, addToQueue } from "../utils.js";
import { startsWith } from "../../utils/index.js";
function __getChild(that, el, ComponentClass, name) {
  const asyncComponentPromise = that.__asyncComponentPromises.get(
    ComponentClass
  );
  if ("$isBase" in ComponentClass || asyncComponentPromise && asyncComponentPromise.status === "resolved") {
    let ctor = ComponentClass;
    if (asyncComponentPromise) {
      ctor = asyncComponentPromise.ctor;
    }
    if (el.__base__ && el.__base__.has(ctor)) {
      return el.__base__.get(ctor);
    }
    const child = new ctor(el);
    Object.defineProperty(child, "$parent", { get: () => that.__base });
    return child;
  }
  const promise = asyncComponentPromise ? asyncComponentPromise.promise : ComponentClass(that.__base);
  if (!asyncComponentPromise) {
    that.__asyncComponentPromises.set(ComponentClass, {
      promise,
      status: "pending",
      ctor: void 0
    });
  }
  return promise.then((module) => {
    const ctor = module.default ?? module;
    that.__asyncComponentPromises.set(ComponentClass, {
      promise,
      status: "resolved",
      ctor
    });
    return __getChild(that, el, ctor, name);
  });
}
function __triggerHookForAll(that, hook) {
  addToQueue(() => {
    for (const name of that.registeredNames) {
      for (const instance of that[name]) {
        if (instance instanceof Promise) {
          instance.then((resolvedInstance) => {
            addToQueue(() => that.__triggerHook(hook, resolvedInstance, name));
          });
        } else {
          addToQueue(() => that.__triggerHook(hook, instance, name));
        }
      }
    }
  });
}
class ChildrenManager extends AbstractManager {
  /**
   * Store async component promises to avoid calling them multiple times and
   * waiting for them when they are already resolved.
   */
  __asyncComponentPromises = /* @__PURE__ */ new WeakMap();
  get registeredNames() {
    return Object.keys(this).filter((key) => !startsWith(key, "__"));
  }
  /**
   * Register instances of all children components.
   */
  registerAll() {
    for (const [name, component] of Object.entries(this.__config.components))
      this.__register(name, component);
  }
  /**
   * Mount all child component instances.
   */
  mountAll() {
    __triggerHookForAll(this, "$mount");
  }
  /**
   * Update all child component instances.
   */
  updateAll() {
    __triggerHookForAll(this, "$update");
  }
  /**
   * Destroy all child component instances.
   */
  destroyAll() {
    __triggerHookForAll(this, "$destroy");
  }
  /**
   * Execute the given hook for the given instance.
   *
   * @param {'$mount'|'$update'|'$destroy'} hook The hook to trigger.
   * @param {Base} instance The target instance.
   * @param {string} name The name of the component.
   * @private
   */
  __triggerHook(hook, instance, name) {
    if (hook === "$update" && !instance.$isMounted) {
      hook = "$mount";
    }
    if (hook === "$update" || hook === "$destroy") {
      this.__eventsManager.unbindChild(name, instance);
    }
    if (hook === "$update" || hook === "$mount") {
      this.__eventsManager.bindChild(name, instance);
    }
    instance[hook]();
  }
  /**
   * Register instance of a child component.
   *
   * @param {string} name
   *   The name of the child component.
   * @param {BaseConstructor|BaseAsyncConstructor} component
   *   A Base class or a Promise for async components.
   * @private
   */
  __register(name, component) {
    Object.defineProperty(this, name, {
      enumerable: true,
      configurable: true,
      get: () => this.__getValue(name, component)
    });
  }
  __getValue(name, component) {
    const elements = getComponentElements(name, this.__element);
    if (elements.length === 0) {
      return [];
    }
    return elements.map((element) => __getChild(this, element, component, name)).filter((instance) => instance !== "terminated");
  }
}
export {
  ChildrenManager
};
//# sourceMappingURL=ChildrenManager.js.map
